// --- Lineup.tsx ---
import {
  AssignedPlayer,
  LookupDetails,
  PlayerInstance,
} from '@alienheadwars/football-card-manager-model';
import React from 'react';
import { PitchDimensions } from '../../../cards/playerCards/CardDimensions'; // Adjusted path
import { SelectablePlayerCard } from '../../../cards/playerCards/SelectablePlayerCard'; // Adjusted path
import { AnimatedBox } from '../../../MotionMui/MotionMui'; // Adjusted path
import { PitchComponent } from '../../play/match/boardComponents/pitch'; // Adjusted path
import { PrintLineupButton } from './PrintableLineup'; // Assuming PrintableLineup.tsx is in the same directory

interface LineupProps {
  players: (AssignedPlayer | PlayerInstance | LookupDetails)[];
  callback?(index: number): void;
  flip?: boolean;
  orientation?: 'vertical' | 'horizontal';
  selectedSlotIndex?: number | null;
  mismatchedFilterIndices?: Set<number>;
}

const playerPositions: { [key: number]: { bottom: number; left: number } } = {
  1: { left: 50, bottom: 10 }, //GK
  2: { left: 15, bottom: 33 }, //LB
  3: { left: 85, bottom: 33 }, //RB
  4: { left: 37, bottom: 28 }, //LCB
  5: { left: 63, bottom: 28 }, //RCB
  6: { left: 20, bottom: 50 }, //LM/LCM
  7: { left: 80, bottom: 50 }, //RM/RCM
  8: { left: 50, bottom: 55 }, //CM/CAM
  9: { left: 20, bottom: 75 }, //LW
  10: { left: 80, bottom: 75 }, //RW
  11: { left: 50, bottom: 80 }, //ST
};

const getPositionStyle = (
  index: number,
  orientation: 'vertical' | 'horizontal' = 'vertical',
  flip?: boolean
): React.CSSProperties => {
  const pos = playerPositions[index + 1] || { bottom: 50, left: 50 };
  const rotate = orientation === 'horizontal';
  const bottomVal = flip ? 100 - pos.bottom : pos.bottom;
  const leftVal = pos.left;
  return {
    position: 'absolute',
    left: `${rotate ? bottomVal : leftVal}%`,
    bottom: `${rotate ? leftVal : bottomVal}%`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 0,
    width: 0,
    overflow: 'visible',
  };
};

export const Lineup = (props: LineupProps) => {
  const {
    players,
    callback,
    flip,
    orientation = 'vertical',
    selectedSlotIndex,
    mismatchedFilterIndices,
  } = props;

  return (
    <>
      <PitchComponent orientation={orientation}>
        <PrintLineupButton players={players} />
        {players.map((playerData, index) => {
          if (!playerData || playerData.id === 'placeholder') return null;

          // No longer resolving with allPlayersMap, playerData is used directly
          const resolvedPlayer:
            | PlayerInstance
            | LookupDetails
            | AssignedPlayer = playerData;

          const positionStyle = getPositionStyle(index, orientation, flip);
          const handleOnClick = callback ? () => callback(index) : undefined;
          const isSelected = selectedSlotIndex === index;
          const isMismatched = !!mismatchedFilterIndices?.has(index);
          const cardSx = isMismatched
            ? { filter: 'grayscale(90%)', opacity: 0.7 }
            : {};

          return (
            <AnimatedBox
              key={playerData.id || `player-${index}`}
              sx={{ ...positionStyle, ...cardSx }}
            >
              <SelectablePlayerCard
                player={resolvedPlayer}
                onClick={handleOnClick}
                selected={isSelected}
                dimensions={PitchDimensions}
                allowExpandWhenNotSelected={!callback}
              />
            </AnimatedBox>
          );
        })}
      </PitchComponent>
    </>
  );
};
